import React from 'react'

/**
 * Composant de pagination responsive utilisant DaisyUI et TailwindCSS.
 * 
 * @component
 * @example
 * // Exemple d'utilisation basique
 * <Pagination
 *   currentPage={3}
 *   totalPages={10}
 *   onPageChange={(page) => handlePageChange(page)}
 * />
 * 
 * @example
 * // Exemple avec options personnalisées
 * <Pagination
 *   currentPage={currentPage}
 *   totalPages={totalPages}
 *   onPageChange={handlePageChange}
 *   maxVisiblePages={7}
 *   previousLabel="Précédent"
 *   nextLabel="Suivant"
 * />
 * 
 * @param {Object} props - Propriétés du composant
 * @param {number} props.currentPage - La page actuellement affichée (commençant à 1)
 * @param {number} props.totalPages - Le nombre total de pages
 * @param {Function} props.onPageChange - Fonction appelée lors du changement de page, reçoit le numéro de page comme argument
 * @param {number} [props.maxVisiblePages=5] - Nombre maximum de boutons de page à afficher (hors points de suspension)
 * @param {string} [props.previousLabel="Précédent"] - Texte du bouton "page précédente"
 * @param {string} [props.nextLabel="Suivant"] - Texte du bouton "page suivante"
 * 
 * @returns {React.ReactElement|null} Le composant de pagination ou null si totalPages <= 1
 */
const Pagination = ({ 
  currentPage, 
  totalPages, 
  onPageChange,
  maxVisiblePages = 5,
  previousLabel = "Précédent",
  nextLabel = "Suivant",
  className = ""
}) => {
  if (totalPages <= 1) return null

  /**
   * Génère un tableau de numéros de page à afficher, incluant potentiellement des points de suspension
   * pour les plages de pages omises.
   * 
   * @returns {Array<number|string>} Tableau contenant les numéros de page et éventuellement des chaînes "..." pour les points de suspension
   */
  const getPageNumbers = () => {
    const pageNumbers = []
    
    if (totalPages <= maxVisiblePages) {
      // Si le nombre total de pages est inférieur ou égal au nombre maximum de pages visibles,
      // afficher toutes les pages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i)
      }
    } else {
      // Sinon, calculer quelles pages afficher
      let startPage
      let endPage
      
      if (currentPage <= Math.ceil(maxVisiblePages / 2)) {
        // Si la page actuelle est proche du début
        startPage = 1
        endPage = maxVisiblePages
      } else if (currentPage + Math.floor(maxVisiblePages / 2) >= totalPages) {
        // Si la page actuelle est proche de la fin
        startPage = totalPages - maxVisiblePages + 1
        endPage = totalPages
      } else {
        // Si la page actuelle est au milieu
        startPage = currentPage - Math.floor(maxVisiblePages / 2)
        endPage = startPage + maxVisiblePages - 1
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i)
      }
      
      // Ajouter des points de suspension si nécessaire
      if (startPage > 1) {
        pageNumbers.unshift('...')
        pageNumbers.unshift(1)
      }
      
      if (endPage < totalPages) {
        pageNumbers.push('...')
        pageNumbers.push(totalPages)
      }
    }
    
    return pageNumbers
  }

  const pageNumbers = getPageNumbers()
  
  return (
    <div className={`flex justify-center ${className.length > 0 ? className : "my-6"}`}>
      <div className="join">
        <button 
          className="join-item btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Page précédente"
        >
          {previousLabel}
        </button>
        
        {pageNumbers.map((pageNumber, index) => (
          <button 
            key={index}
            className={`join-item btn ${pageNumber === currentPage ? 'btn-active' : ''} ${pageNumber === '...' ? 'btn-disabled' : ''}`}
            onClick={() => pageNumber !== '...' && onPageChange(pageNumber)}
            disabled={pageNumber === '...'}
            aria-current={pageNumber === currentPage ? 'page' : undefined}
            aria-label={pageNumber === '...' ? 'Pages omises' : `Page ${pageNumber}`}
          >
            {pageNumber}
          </button>
        ))}
        
        <button 
          className="join-item btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Page suivante"
        >
          {nextLabel}
        </button>
      </div>
    </div>
  )
}

export default Pagination